import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type InviteRow = Database["public"]["Tables"]["project_invites"]["Row"];
type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];

export function Invite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteRow | null>(null);
  const [project, setProject] = useState<ProjectRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!token) {
      setError("Invalid invite link");
      setLoading(false);
      return;
    }

    let isMounted = true;

    supabase
      .from("project_invites")
      .select("*, projects:projects (*)")
      .eq("token", token)
      .single()
      .then(({ data, error: inviteError }) => {
        if (!isMounted) return;

        if (inviteError || !data) {
          setError("Invite not found or expired");
          setLoading(false);
          return;
        }

        const inviteData = data as unknown as InviteRow & { projects: ProjectRow | null };

        if (inviteData.expires_at && new Date(inviteData.expires_at) < new Date()) {
          setError("This invite has expired");
          setLoading(false);
          return;
        }

        setInvite(inviteData);
        setProject(inviteData.projects || null);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [token]);

  async function handleAccept() {
    if (!token) return;

    if (!user) {
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      const { error: acceptError } = await supabase.rpc("accept_invite", {
        invite_token: token,
      });

      if (acceptError) throw acceptError;

      setSuccess(true);
      setTimeout(() => {
        if (project?.id) {
          navigate(`/project/${project.id}`);
        } else {
          navigate("/calendar");
        }
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to accept invite";
      setError(message);
    } finally {
      setAccepting(false);
    }
  }

  if (loading) {
    return (
      <main className="min-h-dvh flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-5xl flex justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <Loader2 className="size-6 animate-spin mx-auto mb-4" />
              <div className="text-muted-foreground">Loading invite…</div>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  if (error || !invite || !project) {
    return (
      <main className="min-h-dvh flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-5xl flex justify-center">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Invalid Invite</CardTitle>
              <CardDescription>
                {error || "This invite link is invalid or has expired"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button asChild className="w-full">
                <Link to="/">Go to Home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  if (success) {
    return (
      <main className="min-h-dvh flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-5xl flex justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="text-green-600 dark:text-green-400 mb-4">✓</div>
              <CardTitle className="mb-2">Invite accepted!</CardTitle>
              <CardDescription>Redirecting to project…</CardDescription>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-5xl flex justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Project Invitation</CardTitle>
            <CardDescription>You've been invited to join a project</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Project</div>
              <div className="font-semibold">{project.name}</div>
              {project.description && (
                <div className="text-sm text-muted-foreground mt-1">{project.description}</div>
              )}
            </div>

            <div>
              <div className="text-sm text-muted-foreground mb-1">Role</div>
              <div className="font-medium capitalize">{invite.role}</div>
            </div>

            {invite.expires_at && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Expires</div>
                <div className="text-sm">{new Date(invite.expires_at).toLocaleDateString()}</div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={handleAccept} disabled={accepting} className="flex-1">
                {accepting ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Accepting…
                  </>
                ) : (
                  "Accept Invite"
                )}
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">Cancel</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}